// @flow

import { selectCartConfigurations } from '@gandi/cart';
import type { Organization } from '@gandi/redux-organization';
import { useTranslator, mapTags } from '@gandi/translate';
import { TextPlaceholder } from '@gandi/ui';

import { useCartSummaryContext } from 'src/Layout/context/CartSummary';
import { useModalContext } from 'src/Layout/context/Modal';
import { PillButton } from 'src/components/PillButton';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';

import { SetupPill } from '../SetupPill';

import { useOwner, type OwnProps as Props } from './hooks';

const resellerProducts = ['DomainCreate', 'CertificateCreate'] as const;

const toRenewProducts = [
  'DomainRenew',
  'MailboxRenew',
  'MailboxStandAloneRenew',
  'MailboxUpdate',
  'Mailboxv2Renew',
  'Mailboxv2Update',
  'MarketplaceRenew',
  'MarketplaceUpdate',
  'PaasRenew',
  'StudioConvert',
] as const;

const attachedToDomainProducts = [
  'MailboxCreate',
  'Mailboxv2Create',
  'PremiumDNSCreate',
] as const;

const otherProducts = [
  'CertificateCreate',
  'CloudAdd',
  'DomainCreate',
  'PaasCreate',
] as const;

function getOrgDisplayName(org: Organization) {
  if (!org) {
    return null;
  }

  if (org.type === 'individual' && org.firstname && org.lastname) {
    return `${org.firstname} ${org.lastname}`;
  }

  return org.orgname || org.name;
}

export function OwnerCellContents(props: Props): null | React.MixedElement {
  const displayNewAccountChoice = useFeatureFlag('FF_SHOP_ACCOUNT_CHOICE');

  const {
    cartId,
    currentOrganization,
    productOwner,
    productOwnerOrg,
    product,
    cartCustomer,
  } = useOwner(props);

  const context = useModalContext();

  const { isReseller } = useCartSummaryContext();

  const { __ } = useTranslator();

  // grab cart configurations to pick the owner_name
  const cartConfigurations = useAppSelector(selectCartConfigurations);
  const cartConfigurationOwnerName = cartConfigurations.find(
    conf =>
      conf.process === product?.process &&
      conf.product_type === product?.productType,
    // $FlowFixMe[prop-missing] check the configurations type from @gandi/cart
  )?.payload.owner_name;

  const concernedProducts = [
    ...new Set([
      ...resellerProducts,
      ...toRenewProducts,
      ...attachedToDomainProducts,
      ...otherProducts,
    ]),
  ];

  // No need for an OwnerCell for products
  // which do not need ownership clarification.
  if (!product || !concernedProducts.includes(product.component)) {
    return null;
  }

  if (!currentOrganization) {
    return null;
  }

  const accountChoiceLink = `/cart/${cartId}/configure/account-choice`;

  function handleChangeProductOwner(evt: SyntheticEvent<HTMLButtonElement>) {
    evt.preventDefault();
    const type = isReseller ? 'customerSelectModal' : 'ownerSelectModal';
    context.openModal({ type, replace: true });
  }

  const isReselling =
    currentOrganization.reseller &&
    cartCustomer &&
    resellerProducts.includes(product.component);

  // Set by account-choice in case of a reseller buying for a customer.
  let ownerName = isReselling
    ? product.payload.owner_name ||
      cartConfigurationOwnerName ||
      __('your customer')
    : // $FlowFixMe[prop-missing]
      getOrgDisplayName(currentOrganization);

  // Case of buying for someone else.
  // XXX: do we need to change the condition to allow `productOwner || productOwnerOrg`
  if (productOwner && otherProducts.includes(product.component)) {
    // $FlowIgnore[incompatible-call]
    ownerName = getOrgDisplayName(productOwnerOrg) || productOwner;
  }

  if (toRenewProducts.includes(product.component)) {
    return <span>{__('Owners will remain the same')}</span>;
  }

  if (attachedToDomainProducts.includes(product.component)) {
    return <span>{__('Owners is same as attached domain')}</span>;
  }

  const extraProps =
    // eslint-disable-next-line no-constant-condition -- TODO: re-enable after https://gitlab.corp.gandi.net/gandi/squad-shop/app-shop/-/work_items/668
    displayNewAccountChoice && false
      ? { onClick: handleChangeProductOwner }
      : {};

  return (
    <OwnerCellContainer
      ownerName={ownerName}
      to={accountChoiceLink}
      {...extraProps}
    />
  );
}

/** Similar to `SetupPill`, but the `icon` is optional. */
function OwnerCellContainer({
  children,
  ownerName,
  to,
  onClick,
}: {
  /** Full content of the Pill (if defined, only the `children` will be rendered, without an `icon`, link or owner) */
  children?: React.MixedElement | string,
  /** Owner */
  ownerName?: ?string,
  /** Link to use on the Pill (if not in `children` mode). Will use a `pen` icon if defined, or a `lock` icon otherwise. */
  to?: string,
  onClick?: (evt: SyntheticEvent<HTMLButtonElement>) => void,
}): React.MixedElement {
  const { __ } = useTranslator();

  let content = children ?? <TextPlaceholder />;

  if (!children && ownerName) {
    content = ownerName;

    content = mapTags(__('Owner: [[ownerName:]]'), () => ({
      ownerName: content,
    }));
  }

  // No icon
  if (children)
    return (
      <PillButton to={to} onClick={onClick} disabled={!to && !onClick}>
        {content}
      </PillButton>
    );

  // With icon
  return (
    <SetupPill to={to} onClick={onClick} disabled={!to && !onClick}>
      {content}
    </SetupPill>
  );
}
